<script setup>
import { useShareLink } from "@/composables/useShareLink";
import { computed } from "vue";
import { Link } from "@inertiajs/vue3";
import { useModalogStore } from "@/stores/modalog.js";
import BrandThrobber from "@/Components/Brand/BrandThrobber.vue";
import IconPrivacyPrivate from "~/svg/privacy-private.svg";
import IconPrivacyPublic from "~/svg/privacy-public.svg";
import IconExpertShield from "~/svg/icon-expert-shield.svg";
import { useInertiaAction } from "@/composables/useInertiaAction.js";
import { useExpertListHeaderImage } from "@/composables/useExpertListHeaderImage.js";

const props = defineProps({
    list: {
        type: Object,
        required: true,
    },
    showActions: {
        type: Boolean,
        default: true,
    },
    // Saved-list rows (BH-29): the trash action removes the bookmark instead
    // of deleting a list the member doesn't own.
    saved: {
        type: Boolean,
        default: false,
    },
    // Pending from elsewhere on the page (GH-1159): the row throbs as if
    // its own action were running.
    processing: {
        type: Boolean,
        default: false,
    },
});

const { showModalog } = useModalogStore();

// Expert lists wear the same navy ribbon as the profile/bookmark cards.
const isExpert = computed(() => props.list.type === "expert");

function deleteList() {
    showModalog({
        type: "ModalogDeleteWishList",
        props: { list: props.list },
    });
}

// Same endpoint + partial reload as the Expert Lists bookmark chip, so the
// sidebar, the header badge and the card chips stay in step; busy spans the
// reload (GH-1159) so the row throbs until it is actually gone.
const { busy: removeBusy, run: runRemove } = useInertiaAction();

// Owner rows throb from the delete modalog's confirm until the reload
// swaps the row out (the modalog flags `is_processing`); saved rows while
// their own remove request + reload run.
const trashBusy = computed(
    () =>
        props.processing ||
        (props.saved ? removeBusy.value : !!props.list.is_processing),
);

function removeBookmark() {
    return runRemove(
        () =>
            axios.delete(
                route("bookmark.destroy", { giftlist: props.list.uuid }),
            ),
        { only: ["savedLists", "savedListsCount", "expertLists"] },
    );
}

// Cover-less lists arrive as Cloudinary's blank "missing-image" fallback,
// not an empty string — treat that as no cover (same as WishListCard).
const hasCover = computed(
    () => !!props.list.image && !props.list.image.includes("missing-image"),
);

// An expert list with a header image shows that on its focal point instead
// of the item photo (GH-1535); a 404 falls back to the item photo.
const { headerImage, headerFailed } = useExpertListHeaderImage(
    () => props.list,
);

const { shareLink } = useShareLink();

async function shareList() {
    // Tag the link before branching so one press mints one token,
    // whichever path the browser takes (GH-1192).
    const channel = navigator.share ? "share_sheet" : "copy_link";
    const url = shareLink(
        props.list.url,
        { type: "giftlist", uuid: props.list.uuid },
        channel,
    );

    if (navigator.share) {
        try {
            await navigator.share({ title: props.list.name, url });
        } catch {
            // User dismissed the share sheet — nothing to do.
        }

        return;
    }

    await navigator.clipboard?.writeText(url);
}
</script>

<template>
    <article
        class="relative flex items-stretch gap-3 overflow-hidden rounded-brand border border-brand-tan/60 bg-white p-3"
    >
        <!-- Bleeds off the card's left edge like BookmarkCard; the text
             column drops below it so the title never sits under the tag. -->
        <span
            v-if="isExpert"
            class="absolute left-0 top-3 inline-flex items-center gap-1.5 rounded-r-[2px] bg-brand-navy py-1 pl-[18px] pr-3 font-brand text-[11px] font-bold tracking-wide text-white"
        >
            <IconExpertShield class="h-3.5 w-3 shrink-0" aria-hidden="true" />
            EXPERT LIST
        </span>

        <div class="flex min-w-0 flex-1 flex-col" :class="{ 'pt-8': isExpert }">
            <Link
                :href="list.url"
                class="font-brand text-[16px] font-bold leading-[1.2] text-[#102a3d] transition hover:text-brand-red"
            >
                {{ list.name }}
            </Link>
            <p
                v-if="list.description"
                class="mt-1 line-clamp-2 font-brand text-[13px] leading-[1.3] text-[#565a77]"
            >
                {{ list.description }}
            </p>

            <div class="mt-auto flex items-center gap-2 pt-3">
                <!-- Privacy badge -->
                <span
                    class="inline-flex items-center gap-1.5 rounded-[2px] px-2.5 py-1 text-[11px] font-medium capitalize text-[#102a3d]"
                    :class="
                        list.privacy === 'private'
                            ? 'bg-[#f0ece9]'
                            : 'border border-solid border-[#e9e8e0]'
                    "
                >
                    <IconPrivacyPrivate
                        v-if="list.privacy === 'private'"
                        class="h-[9.5px] w-[11px] shrink-0 text-[#102a3d]"
                        aria-hidden="true"
                    />
                    <IconPrivacyPublic
                        v-else
                        class="size-[10.3px] shrink-0 text-[#102a3d]"
                        aria-hidden="true"
                    />
                    {{ list.privacy }}
                </span>

                <template v-if="showActions">
                    <button
                        type="button"
                        class="ml-auto grid size-7 place-items-center rounded-[4px] text-[#102a3d]/50 transition hover:bg-brand-cream hover:text-brand-navy"
                        aria-label="Share list"
                        @click="shareList"
                    >
                        <svg
                            viewBox="0 0 14 16"
                            class="h-4 w-3.5"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.4"
                            aria-hidden="true"
                        >
                            <path
                                d="M7 10V1m0 0L4 4m3-3l3 3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M2 7H1v8h12V7h-1"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                    <button
                        type="button"
                        class="grid size-7 place-items-center rounded-[4px] text-[#102a3d]/50 transition hover:bg-brand-cream hover:text-brand-red disabled:opacity-50"
                        :aria-label="
                            saved ? 'Remove from Saved Lists' : 'Delete list'
                        "
                        :disabled="trashBusy"
                        @click="saved ? removeBookmark() : deleteList()"
                    >
                        <!-- The row outlives the request until the reload
                             lands, so the trash gives way to the dots. -->
                        <BrandThrobber
                            v-if="trashBusy"
                            variant="mono"
                            class="text-[12px] text-brand-red"
                        />
                        <svg
                            v-else
                            viewBox="0 0 12 14"
                            class="h-4 w-3.5"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.4"
                            aria-hidden="true"
                        >
                            <path
                                d="M1 3h10M4 3V1.5h4V3M2.5 3l.5 9.5h6L9.5 3"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </template>
            </div>
        </div>

        <Link
            :href="list.url"
            class="grid aspect-[102/115] w-[88px] shrink-0 place-items-center overflow-hidden rounded-[4px] bg-brand-tan/30"
        >
            <img
                v-if="headerImage"
                :src="headerImage.tile2xSrc"
                :alt="list.name"
                class="size-full object-cover"
                :style="{ objectPosition: headerImage.position }"
                data-test="list-header-photo"
                @error="headerFailed = true"
            />
            <img
                v-else-if="hasCover"
                :src="list.image"
                :alt="list.name"
                class="size-full object-cover"
            />
            <img
                v-else
                :src="asset('images/brand/logo-mark.png')"
                alt=""
                aria-hidden="true"
                class="size-9 opacity-25 grayscale"
            />
        </Link>
    </article>
</template>
